// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { AzureClouds, AzureSupportedClouds } from "./static-helpers/cloudSettingHelpers.js";
import {
  PageSettings,
  ContinuablePage,
  PagedAsyncIterableIterator,
} from "./static-helpers/pagingHelpers.js";

export { AzureVMwareSolutionAPI } from "./azureVMwareSolutionAPI.js";
export { restorePoller, RestorePollerOptions } from "./restorePollerHelpers.js";
export {
  Operation,
  OperationDisplay,
  KnownOrigin,
  Origin,
  KnownActionType,
  ActionType,
  ErrorResponse,
  ErrorDetail,
  ErrorAdditionalInfo,
  Addon,
  AddonProperties,
  AddonPropertiesUnion,
  KnownAddonType,
  AddonType,
  KnownAddonProvisioningState,
  AddonProvisioningState,
  AddonSrmProperties,
  AddonVrProperties,
  AddonHcxProperties,
  AddonArcProperties,
  ProxyResource,
  Resource,
  SystemData,
  KnownCreatedByType,
  CreatedByType,
  ExpressRouteAuthorization,
  ExpressRouteAuthorizationProperties,
  KnownExpressRouteAuthorizationProvisioningState,
  ExpressRouteAuthorizationProvisioningState,
  CloudLink,
  CloudLinkProperties,
  KnownCloudLinkProvisioningState,
  CloudLinkProvisioningState,
  KnownCloudLinkStatus,
  CloudLinkStatus,
  Cluster,
  ClusterProperties,
  KnownClusterProvisioningState,
  ClusterProvisioningState,
  Sku,
  SkuTier,
  ClusterUpdate,
  ClusterUpdateProperties,
  ClusterZoneList,
  ClusterZone,
  Datastore,
  DatastoreProperties,
  KnownDatastoreProvisioningState,
  DatastoreProvisioningState,
  NetAppVolume,
  DiskPoolVolume,
  KnownMountOptionEnum,
  MountOptionEnum,
  ElasticSanVolume,
  PureStorageVolume,
  KnownDatastoreStatus,
  DatastoreStatus,
  GlobalReachConnection,
  GlobalReachConnectionProperties,
  KnownGlobalReachConnectionProvisioningState,
  GlobalReachConnectionProvisioningState,
  KnownGlobalReachConnectionStatus,
  GlobalReachConnectionStatus,
  HcxEnterpriseSite,
  HcxEnterpriseSiteProperties,
  KnownHcxEnterpriseSiteProvisioningState,
  HcxEnterpriseSiteProvisioningState,
  KnownHcxEnterpriseSiteStatus,
  HcxEnterpriseSiteStatus,
  Host,
  HostProperties,
  HostPropertiesUnion,
  KnownHostKind,
  HostKind,
  KnownHostProvisioningState,
  HostProvisioningState,
  KnownHostMaintenance,
  HostMaintenance,
  GeneralHostProperties,
  SpecializedHostProperties,
  IscsiPath,
  IscsiPathProperties,
  KnownIscsiPathProvisioningState,
  IscsiPathProvisioningState,
  License,
  LicenseProperties,
  LicensePropertiesUnion,
  KnownLicenseKind,
  LicenseKind,
  KnownLicenseProvisioningState,
  LicenseProvisioningState,
  VmwareFirewallLicenseProperties,
  Label,
  KnownLicenseName,
  LicenseName,
  Trial,
  KnownTrialStatus,
  TrialStatus,
  Quota,
  KnownQuotaEnabled,
  QuotaEnabled,
  Maintenance,
  MaintenanceProperties,
  KnownMaintenanceType,
  MaintenanceType,
  MaintenanceState,
  KnownMaintenanceStateName,
  MaintenanceStateName,
  KnownMaintenanceProvisioningState,
  MaintenanceProvisioningState,
  MaintenanceManagementOperation,
  MaintenanceManagementOperationUnion,
  KnownMaintenanceManagementOperationKind,
  MaintenanceManagementOperationKind,
  ScheduleOperation,
  ScheduleOperationConstraint,
  ScheduleOperationConstraintUnion,
  KnownScheduleOperationConstraintKind,
  ScheduleOperationConstraintKind,
  SchedulingWindow,
  AvailableWindowForMaintenanceWhileScheduleOperation,
  BlockedWhileScheduleOperation,
  KnownBlockedDatesConstraintCategory,
  BlockedDatesConstraintCategory,
  BlockedDatesConstraintTimeRange,
  RescheduleOperation,
  RescheduleOperationConstraint,
  RescheduleOperationConstraintUnion,
  KnownRescheduleOperationConstraintKind,
  RescheduleOperationConstraintKind,
  AvailableWindowForMaintenanceWhileRescheduleOperation,
  BlockedWhileRescheduleOperation,
  MaintenanceReadinessRefreshOperation,
  KnownMaintenanceReadinessRefreshOperationStatus,
  MaintenanceReadinessRefreshOperationStatus,
  MaintenanceReadiness,
  KnownMaintenanceCheckType,
  MaintenanceCheckType,
  KnownMaintenanceReadinessStatus,
  MaintenanceReadinessStatus,
  MaintenanceFailedCheck,
  ImpactedMaintenanceResource,
  ImpactedMaintenanceResourceError,
  MaintenanceReschedule,
  MaintenanceSchedule,
  PlacementPolicy,
  PlacementPolicyProperties,
  PlacementPolicyPropertiesUnion,
  KnownPlacementPolicyType,
  PlacementPolicyType,
  KnownPlacementPolicyState,
  PlacementPolicyState,
  KnownPlacementPolicyProvisioningState,
  PlacementPolicyProvisioningState,
  VmPlacementPolicyProperties,
  KnownAffinityType,
  AffinityType,
  VmHostPlacementPolicyProperties,
  KnownAffinityStrength,
  AffinityStrength,
  KnownAzureHybridBenefitType,
  AzureHybridBenefitType,
  PlacementPolicyUpdate,
  PlacementPolicyUpdateProperties,
  PrivateCloud,
  PrivateCloudProperties,
  ManagementCluster,
  KnownInternetEnum,
  InternetEnum,
  IdentitySource,
  KnownSslEnum,
  SslEnum,
  AvailabilityProperties,
  KnownAvailabilityStrategy,
  AvailabilityStrategy,
  Encryption,
  KnownEncryptionState,
  EncryptionState,
  EncryptionKeyVaultProperties,
  KnownEncryptionKeyStatus,
  EncryptionKeyStatus,
  KnownEncryptionVersionType,
  EncryptionVersionType,
  KnownPrivateCloudProvisioningState,
  PrivateCloudProvisioningState,
  Circuit,
  Endpoints,
  KnownNsxPublicIpQuotaRaisedEnum,
  NsxPublicIpQuotaRaisedEnum,
  KnownDnsZoneType,
  DnsZoneType,
  VcfLicense,
  VcfLicenseUnion,
  KnownVcfLicenseKind,
  VcfLicenseKind,
  Vcf5License,
  PrivateCloudIdentity,
  KnownResourceIdentityType,
  ResourceIdentityType,
  TrackedResource,
  PrivateCloudUpdate,
  PrivateCloudUpdateProperties,
  AdminCredentials,
  ProvisionedNetwork,
  ProvisionedNetworkProperties,
  KnownProvisionedNetworkProvisioningState,
  ProvisionedNetworkProvisioningState,
  KnownProvisionedNetworkTypes,
  ProvisionedNetworkTypes,
  PureStoragePolicy,
  PureStoragePolicyProperties,
  KnownPureStoragePolicyProvisioningState,
  PureStoragePolicyProvisioningState,
  ScriptCmdlet,
  ScriptCmdletProperties,
  KnownScriptCmdletProvisioningState,
  ScriptCmdletProvisioningState,
  KnownScriptCmdletAudience,
  ScriptCmdletAudience,
  ScriptParameter,
  KnownScriptParameterTypes,
  ScriptParameterTypes,
  KnownVisibilityParameterEnum,
  VisibilityParameterEnum,
  KnownOptionalParamEnum,
  OptionalParamEnum,
  ScriptExecution,
  ScriptExecutionProperties,
  ScriptExecutionParameter,
  ScriptExecutionParameterUnion,
  KnownScriptExecutionParameterType,
  ScriptExecutionParameterType,
  ScriptSecureStringExecutionParameter,
  ScriptStringExecutionParameter,
  PSCredentialExecutionParameter,
  KnownScriptExecutionProvisioningState,
  ScriptExecutionProvisioningState,
  ScriptPackage,
  ScriptPackageProperties,
  KnownScriptPackageProvisioningState,
  ScriptPackageProvisioningState,
  ResourceSku,
  KnownResourceSkuResourceType,
  ResourceSkuResourceType,
  ResourceSkuLocationInfo,
  ResourceSkuZoneDetails,
  ResourceSkuCapabilities,
  ResourceSkuRestrictions,
  KnownResourceSkuRestrictionsType,
  ResourceSkuRestrictionsType,
  ResourceSkuRestrictionInfo,
  KnownResourceSkuRestrictionsReasonCode,
  ResourceSkuRestrictionsReasonCode,
  VirtualMachine,
  VirtualMachineProperties,
  KnownVirtualMachineProvisioningState,
  VirtualMachineProvisioningState,
  KnownVirtualMachineRestrictMovementState,
  VirtualMachineRestrictMovementState,
  VirtualMachineRestrictMovement,
  WorkloadNetwork,
  WorkloadNetworkProperties,
  KnownWorkloadNetworkProvisioningState,
  WorkloadNetworkProvisioningState,
  WorkloadNetworkDhcp,
  WorkloadNetworkDhcpEntity,
  WorkloadNetworkDhcpEntityUnion,
  KnownDhcpTypeEnum,
  DhcpTypeEnum,
  KnownWorkloadNetworkDhcpProvisioningState,
  WorkloadNetworkDhcpProvisioningState,
  WorkloadNetworkDhcpServer,
  WorkloadNetworkDhcpRelay,
  WorkloadNetworkDnsService,
  WorkloadNetworkDnsServiceProperties,
  KnownDnsServiceLogLevelEnum,
  DnsServiceLogLevelEnum,
  KnownDnsServiceStatusEnum,
  DnsServiceStatusEnum,
  KnownWorkloadNetworkDnsServiceProvisioningState,
  WorkloadNetworkDnsServiceProvisioningState,
  WorkloadNetworkDnsZone,
  WorkloadNetworkDnsZoneProperties,
  KnownWorkloadNetworkDnsZoneProvisioningState,
  WorkloadNetworkDnsZoneProvisioningState,
  WorkloadNetworkGateway,
  WorkloadNetworkGatewayProperties,
  WorkloadNetworkPortMirroring,
  WorkloadNetworkPortMirroringProperties,
  KnownPortMirroringDirectionEnum,
  PortMirroringDirectionEnum,
  KnownPortMirroringStatusEnum,
  PortMirroringStatusEnum,
  KnownWorkloadNetworkPortMirroringProvisioningState,
  WorkloadNetworkPortMirroringProvisioningState,
  WorkloadNetworkPublicIP,
  WorkloadNetworkPublicIPProperties,
  KnownWorkloadNetworkPublicIPProvisioningState,
  WorkloadNetworkPublicIPProvisioningState,
  WorkloadNetworkSegment,
  WorkloadNetworkSegmentProperties,
  WorkloadNetworkSegmentSubnet,
  WorkloadNetworkSegmentPortVif,
  KnownSegmentStatusEnum,
  SegmentStatusEnum,
  KnownWorkloadNetworkSegmentProvisioningState,
  WorkloadNetworkSegmentProvisioningState,
  WorkloadNetworkVirtualMachine,
  WorkloadNetworkVirtualMachineProperties,
  KnownVMTypeEnum,
  VMTypeEnum,
  WorkloadNetworkVMGroup,
  WorkloadNetworkVMGroupProperties,
  KnownVMGroupStatusEnum,
  VMGroupStatusEnum,
  KnownWorkloadNetworkVMGroupProvisioningState,
  WorkloadNetworkVMGroupProvisioningState,
  KnownMaintenanceStatusFilter,
  MaintenanceStatusFilter,
  KnownScriptOutputStreamType,
  ScriptOutputStreamType,
  KnownVersions,
} from "./models/index.js";
export { AzureVMwareSolutionAPIOptionalParams } from "./api/index.js";
export {
  AddonsDeleteOptionalParams,
  AddonsCreateOrUpdateOptionalParams,
  AddonsGetOptionalParams,
  AddonsListOptionalParams,
} from "./api/addons/index.js";
export {
  AuthorizationsDeleteOptionalParams,
  AuthorizationsCreateOrUpdateOptionalParams,
  AuthorizationsGetOptionalParams,
  AuthorizationsListOptionalParams,
} from "./api/authorizations/index.js";
export {
  CloudLinksDeleteOptionalParams,
  CloudLinksCreateOrUpdateOptionalParams,
  CloudLinksGetOptionalParams,
  CloudLinksListOptionalParams,
} from "./api/cloudLinks/index.js";
export {
  ClustersListZonesOptionalParams,
  ClustersDeleteOptionalParams,
  ClustersUpdateOptionalParams,
  ClustersCreateOrUpdateOptionalParams,
  ClustersGetOptionalParams,
  ClustersListOptionalParams,
} from "./api/clusters/index.js";
export {
  DatastoresDeleteOptionalParams,
  DatastoresCreateOrUpdateOptionalParams,
  DatastoresGetOptionalParams,
  DatastoresListOptionalParams,
} from "./api/datastores/index.js";
export {
  GlobalReachConnectionsDeleteOptionalParams,
  GlobalReachConnectionsCreateOrUpdateOptionalParams,
  GlobalReachConnectionsGetOptionalParams,
  GlobalReachConnectionsListOptionalParams,
} from "./api/globalReachConnections/index.js";
export {
  HcxEnterpriseSitesDeleteOptionalParams,
  HcxEnterpriseSitesCreateOrUpdateOptionalParams,
  HcxEnterpriseSitesGetOptionalParams,
  HcxEnterpriseSitesListOptionalParams,
} from "./api/hcxEnterpriseSites/index.js";
export { HostsGetOptionalParams, HostsListOptionalParams } from "./api/hosts/index.js";
export {
  IscsiPathsDeleteOptionalParams,
  IscsiPathsCreateOrUpdateOptionalParams,
  IscsiPathsGetOptionalParams,
  IscsiPathsListByPrivateCloudOptionalParams,
} from "./api/iscsiPaths/index.js";
export {
  LicensesGetPropertiesOptionalParams,
  LicensesDeleteOptionalParams,
  LicensesCreateOrUpdateOptionalParams,
  LicensesGetOptionalParams,
  LicensesListOptionalParams,
} from "./api/licenses/index.js";
export {
  LocationsCheckQuotaAvailabilityOptionalParams,
  LocationsCheckTrialAvailabilityOptionalParams,
} from "./api/locations/index.js";
export {
  MaintenancesInitiateChecksOptionalParams,
  MaintenancesScheduleOptionalParams,
  MaintenancesRescheduleOptionalParams,
  MaintenancesGetOptionalParams,
  MaintenancesListOptionalParams,
} from "./api/maintenances/index.js";
export { OperationsListOptionalParams } from "./api/operations/index.js";
export {
  PlacementPoliciesDeleteOptionalParams,
  PlacementPoliciesUpdateOptionalParams,
  PlacementPoliciesCreateOrUpdateOptionalParams,
  PlacementPoliciesGetOptionalParams,
  PlacementPoliciesListOptionalParams,
} from "./api/placementPolicies/index.js";
export {
  PrivateCloudsGetVcfLicenseOptionalParams,
  PrivateCloudsListAdminCredentialsOptionalParams,
  PrivateCloudsRotateNsxtPasswordOptionalParams,
  PrivateCloudsRotateVcenterPasswordOptionalParams,
  PrivateCloudsDeleteOptionalParams,
  PrivateCloudsUpdateOptionalParams,
  PrivateCloudsCreateOrUpdateOptionalParams,
  PrivateCloudsGetOptionalParams,
  PrivateCloudsListInSubscriptionOptionalParams,
  PrivateCloudsListOptionalParams,
} from "./api/privateClouds/index.js";
export {
  ProvisionedNetworksGetOptionalParams,
  ProvisionedNetworksListOptionalParams,
} from "./api/provisionedNetworks/index.js";
export {
  PureStoragePoliciesDeleteOptionalParams,
  PureStoragePoliciesCreateOrUpdateOptionalParams,
  PureStoragePoliciesGetOptionalParams,
  PureStoragePoliciesListOptionalParams,
} from "./api/pureStoragePolicies/index.js";
export {
  ScriptCmdletsGetOptionalParams,
  ScriptCmdletsListOptionalParams,
} from "./api/scriptCmdlets/index.js";
export {
  ScriptExecutionsGetExecutionLogsOptionalParams,
  ScriptExecutionsDeleteOptionalParams,
  ScriptExecutionsCreateOrUpdateOptionalParams,
  ScriptExecutionsGetOptionalParams,
  ScriptExecutionsListOptionalParams,
} from "./api/scriptExecutions/index.js";
export {
  ScriptPackagesGetOptionalParams,
  ScriptPackagesListOptionalParams,
} from "./api/scriptPackages/index.js";
export { SkusListOptionalParams } from "./api/skus/index.js";
export {
  VirtualMachinesRestrictMovementOptionalParams,
  VirtualMachinesGetOptionalParams,
  VirtualMachinesListOptionalParams,
} from "./api/virtualMachines/index.js";
export {
  WorkloadNetworksDeleteVMGroupOptionalParams,
  WorkloadNetworksUpdateVMGroupOptionalParams,
  WorkloadNetworksCreateVMGroupOptionalParams,
  WorkloadNetworksGetVMGroupOptionalParams,
  WorkloadNetworksListVMGroupsOptionalParams,
  WorkloadNetworksGetVirtualMachineOptionalParams,
  WorkloadNetworksListVirtualMachinesOptionalParams,
  WorkloadNetworksDeleteSegmentOptionalParams,
  WorkloadNetworksUpdateSegmentsOptionalParams,
  WorkloadNetworksCreateSegmentsOptionalParams,
  WorkloadNetworksGetSegmentOptionalParams,
  WorkloadNetworksListSegmentsOptionalParams,
  WorkloadNetworksDeletePublicIPOptionalParams,
  WorkloadNetworksCreatePublicIPOptionalParams,
  WorkloadNetworksGetPublicIPOptionalParams,
  WorkloadNetworksListPublicIPsOptionalParams,
  WorkloadNetworksDeletePortMirroringOptionalParams,
  WorkloadNetworksUpdatePortMirroringOptionalParams,
  WorkloadNetworksCreatePortMirroringOptionalParams,
  WorkloadNetworksGetPortMirroringOptionalParams,
  WorkloadNetworksListPortMirroringOptionalParams,
  WorkloadNetworksGetGatewayOptionalParams,
  WorkloadNetworksListGatewaysOptionalParams,
  WorkloadNetworksDeleteDnsZoneOptionalParams,
  WorkloadNetworksUpdateDnsZoneOptionalParams,
  WorkloadNetworksCreateDnsZoneOptionalParams,
  WorkloadNetworksGetDnsZoneOptionalParams,
  WorkloadNetworksListDnsZonesOptionalParams,
  WorkloadNetworksDeleteDnsServiceOptionalParams,
  WorkloadNetworksUpdateDnsServiceOptionalParams,
  WorkloadNetworksCreateDnsServiceOptionalParams,
  WorkloadNetworksGetDnsServiceOptionalParams,
  WorkloadNetworksListDnsServicesOptionalParams,
  WorkloadNetworksDeleteDhcpOptionalParams,
  WorkloadNetworksUpdateDhcpOptionalParams,
  WorkloadNetworksCreateDhcpOptionalParams,
  WorkloadNetworksGetDhcpOptionalParams,
  WorkloadNetworksListDhcpOptionalParams,
  WorkloadNetworksListOptionalParams,
  WorkloadNetworksGetOptionalParams,
} from "./api/workloadNetworks/index.js";
export {
  AddonsOperations,
  AuthorizationsOperations,
  CloudLinksOperations,
  ClustersOperations,
  DatastoresOperations,
  GlobalReachConnectionsOperations,
  HcxEnterpriseSitesOperations,
  HostsOperations,
  IscsiPathsOperations,
  LicensesOperations,
  LocationsOperations,
  MaintenancesOperations,
  OperationsOperations,
  PlacementPoliciesOperations,
  PrivateCloudsOperations,
  ProvisionedNetworksOperations,
  PureStoragePoliciesOperations,
  ScriptCmdletsOperations,
  ScriptExecutionsOperations,
  ScriptPackagesOperations,
  SkusOperations,
  VirtualMachinesOperations,
  WorkloadNetworksOperations,
} from "./classic/index.js";
export { PageSettings, ContinuablePage, PagedAsyncIterableIterator };
export { AzureClouds, AzureSupportedClouds };
